import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { BridgeAdUnit } from '../domain/bridge-ad-unit.model';
import { HttpUtils } from '../utils/http-utils';
import { BridgeAdUnitInventoryResponse } from '../domain/bridge-ad-unit-inventory-response.model';
import { BridgeAdUnitInventoryRequest } from '../domain/bridge-ad-unit-inventory-request.model';
import { AdUnitIoCpm } from '../../kitchen/shared/domain/ad-unit-io-cpm.model';
import { AdUnitRevshare } from '../../kitchen/shared/domain/ad-unit-revshare.model';
import { CreateAdUnitRequest, MultiSizeCreateUnitResponse } from '../../kitchen/shared/domain/bridge-ad-unit-create-request.model';
import { BridgeAdUnitDpHbId } from '../domain/bridge-ad-unit-hb-id.model';
import { AdConfig } from '../../kitchen/shared/domain/ad-config.model';
import { AdUnitBidderRevshare } from '../../kitchen/shared/domain/ad-unit-bidder-revshare.model';
import { AdUnitBidderCostFactor } from '../../kitchen/shared/domain/ad-unit-bidder-cost-factor.model';
import { BridgeAdUnitHbIdInventoryRequest } from '../domain/bridge-ad-unit-hbid-inventory-request.model';
import { BridgeAdUnitHbResponse } from '../domain/bridge-ad-unit-hb-response.model';
import { AdUnitNote } from '../domain/ad-unit-note.model';
import { ChangeLogLine } from '../domain/bridge-ad-unit-change-log.model';
import { IabContentTaxonomyT1 } from '../domain/iab-content-taxonomy.model';
import { AdUnitBidIncrement } from '../../kitchen/shared/domain/ad-unit-bid-increment.model';
import { AdUnitValueHistory, AdUnitValueHistoryType } from '../../kitchen/shared/domain/ad-unit-value-history.model';
import {
  BidParamChangeRequest,
  BidParamChangeRequestFilter,
  BidParamChangeStatus} from '../domain/bid-param-change.model';

@Injectable()
export class AdStatsBridgeAdUnitsService {

  constructor(private httpClient: HttpClient) {
  }

  adUnitsLight(): Observable<BridgeAdUnit[]> {
    return this.httpClient.get<BridgeAdUnit[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units-light`);
  }

  adUnitGet(id: string): Observable<BridgeAdUnit> {
    return this.httpClient.get<BridgeAdUnit>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${id}`);
  }

  adUnitPut(unit: BridgeAdUnit): Observable<BridgeAdUnit> {
    return this.httpClient.put<BridgeAdUnit>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${unit.id}`, unit);
  }

  /**
   * convert unit to s2s
   */
  adUnitPutS2S(unit: BridgeAdUnit): Observable<BridgeAdUnit> {
    return this.httpClient.put<BridgeAdUnit>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${unit.id}/s2s`, null, {
      params: HttpUtils.getHttpParams({
        s2s: true
      })
    });
  }

  adUnitsCurrentConfig(id: string): Observable<AdConfig> {
    return this.httpClient.get<AdConfig>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${id}/current-config`);
  }

  adUnitsChangelog(id: string): Observable<{ string: ChangeLogLine[] }> {
    return this.httpClient.get<{ string: ChangeLogLine[] }>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${id}/change-logcomment`);
  }

  adUnitsInventory(params): Observable<BridgeAdUnitInventoryResponse> {
    return this.httpClient.get<BridgeAdUnitInventoryResponse>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units-inventory`, {
      params: HttpUtils.getHttpParams(params)
    });
  }

  adUnitsNoteGet(unitId: string): Observable<AdUnitNote[]> {
    return this.httpClient.get<AdUnitNote[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${unitId}/note`);
  }

  adUnitsNotePost(unitId: string, note: AdUnitNote): Observable<AdUnitNote> {
    return this.httpClient.post<AdUnitNote>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${unitId}/note`, note);
  }

  adUnitsNotePut(note: AdUnitNote): Observable<AdUnitNote> {
    return this.httpClient.put<AdUnitNote>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/note/${note.id}`, note);
  }

  adUnitsNoteDelete(note: AdUnitNote) {
    return this.httpClient.delete(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/note/${note.id}`);
  }

  adUnitsDpHb(params: BridgeAdUnitHbIdInventoryRequest): Observable<BridgeAdUnitHbResponse> {
    return this.httpClient.get<BridgeAdUnitHbResponse>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units-dp-hb`, {
      params: HttpUtils.getHttpParams(params)
    });
  }

  adUnitsDpHbStatusChanges(params: BridgeAdUnitHbIdInventoryRequest): Observable<BridgeAdUnitHbResponse> {
    return this.httpClient.post<BridgeAdUnitHbResponse>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units-dp-hb/status-changes`, params);
  }

  adUnitsDpHbExport(filter: BridgeAdUnitHbIdInventoryRequest) {
    return this.httpClient.get(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units-dp-hb/export`, {
      responseType: 'text',
      params: HttpUtils.getHttpParams(filter)
    });
  }

  adUnitsDpHbStatusChangesExport(filter: BridgeAdUnitHbIdInventoryRequest) {
    return this.httpClient.post(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units-dp-hb/status-changes/export`, filter, {
      responseType: 'text',
    });
  }

  adUnitsRebuild(params: { notes: string, adUnitIds: string[] }): Observable<any> {
    return this.httpClient.post(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units-rebuild`, params);
  }

  adUnitsRebuildSendUpdateEmail(params: { notes: string, adUnitIds: string[] }): Observable<any> {
    return this.httpClient.post(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units-hb-update-email`, params);
  }

  adUnitsTeamSupportEmail(params: { emails: string[], comment: string, adUnitIds: string[] }): Observable<any> {
    return this.httpClient.post(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units-team-email`, params);
  }

  searchAdUnitByName(name: string, limit = 20): Observable<BridgeAdUnit[]> {
    return this.httpClient.get<BridgeAdUnit[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/find-by-name?name=${name}&limit=${limit}`);
  }

  adUnitsTaxonomies(): Observable<string[]> {
    return this.httpClient.get<string[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/taxonomies`);
  }

  adUnitsTaxonomiesFull(): Observable<IabContentTaxonomyT1[]> {
    return this.httpClient.get<IabContentTaxonomyT1[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/taxonomies-full`);
  }

  findAllAdUnitsByIdIn(adUnitIds: string[]): Observable<BridgeAdUnit[]> {
    return this.httpClient.get<BridgeAdUnit[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/find-by-id`, {
      params: HttpUtils.getHttpParams({
        ids: adUnitIds
      })
    });
  }

  adUnitsInventoryExport(filter: BridgeAdUnitInventoryRequest) {
    return this.httpClient.get(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units-inventory/export`, {
      responseType: 'text',
      params: HttpUtils.getHttpParams(filter)
    });
  }

  adUnitsIndexLoadSheetExport(filter: BridgeAdUnitInventoryRequest) {
    return this.httpClient.get(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units-inventory/export-index`, {
      responseType: 'text',
      params: HttpUtils.getHttpParams(filter)
    });
  }

  archiveAdUnit(unitId: string, adUnitNote: AdUnitNote): Observable<BridgeAdUnit> {
    return this.httpClient.put<BridgeAdUnit>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${unitId}/archive`, adUnitNote);
  }

  adUnitsBidderDp(adUnit: BridgeAdUnit) {
    return this.httpClient.post(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${adUnit.id}/bidder-dp`, adUnit);
  }

  adUnitsCreateOpenxUnit(adUnitId: string): Observable<BridgeAdUnitDpHbId> {
    return this.httpClient.post<BridgeAdUnitDpHbId>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${adUnitId}/create-openx-unit`, null);
  }

  adUnitsCreateSovrnUnit(adUnitId: string): Observable<BridgeAdUnitDpHbId> {
    return this.httpClient.post<BridgeAdUnitDpHbId>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${adUnitId}/create-sovrn-unit`, null);
  }

  adUnitRecreatePixel(adUnitId: string) {
    return this.httpClient.post(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${adUnitId}/recreate-pixel`, null);
  }

  adUnitRecreateDfpUnit(adUnitId: string) {
    return this.httpClient.post(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${adUnitId}/recreate-dfpunit`, null);
  }

  adUnitPost(create: CreateAdUnitRequest): Observable<MultiSizeCreateUnitResponse> {
    return this.httpClient.post<MultiSizeCreateUnitResponse>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units2`, create);
  }


  adUnitsCreatePixel(id: string): Observable<BridgeAdUnit> {
    return this.httpClient.post<BridgeAdUnit>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${id}/create-pixel`, {});
  }

  hbIdGet(unitId: string): Observable<BridgeAdUnitDpHbId[]> {
    return this.httpClient.get<BridgeAdUnitDpHbId[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${unitId}/hb-id`);
  }

  hbIdPost(unitId: string, hbIds: BridgeAdUnitDpHbId[]): Observable<BridgeAdUnitDpHbId[]> {
    return this.httpClient.post<BridgeAdUnitDpHbId[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${unitId}/hb-id`, hbIds);
  }

  hbIdPostHealthCheck(unitId: string, hbId: BridgeAdUnitDpHbId, healthyStatus: boolean): Observable<BridgeAdUnit> {
    return this.httpClient.put<BridgeAdUnit>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${unitId}/hb-id/${hbId.id}/dp-post-health-check?healthy=${healthyStatus}`, null);
  }

  hbIdDelete(unitId: string, hbId: BridgeAdUnitDpHbId) {
    return this.httpClient.delete(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${unitId}/hb-id/${hbId.id}`);
  }

  // finance
  ioCpmGet(bridgeAdUnitId: string): Observable<AdUnitIoCpm[]> {
    return this.httpClient.get<AdUnitIoCpm[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/iocpm`);
  }

  ioCpmPost(bridgeAdUnitId: string, ioCpmData: AdUnitIoCpm[]): Observable<AdUnitIoCpm[]> {
    return this.httpClient.post<AdUnitIoCpm[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/iocpm`, ioCpmData);
  }

  ioCpmDelete(bridgeAdUnitId: string, ioCpmId: number) {
    return this.httpClient.delete(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/iocpm/${ioCpmId}`);
  }

  revshareGet(bridgeAdUnitId: string): Observable<AdUnitRevshare[]> {
    return this.httpClient.get<AdUnitRevshare[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/revshare`);
  }

  revsharePost(bridgeAdUnitId: string, data: AdUnitRevshare[]): Observable<AdUnitRevshare[]> {
    return this.httpClient.post<AdUnitRevshare[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/revshare`, data);
  }

  revshareDelete(bridgeAdUnitId: string, id: number) {
    return this.httpClient.delete(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/revshare/${id}`);
  }

  valueHistoryGet(bridgeAdUnitId: string, type: AdUnitValueHistoryType): Observable<AdUnitValueHistory[]> {
    return this.httpClient.get<AdUnitValueHistory[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/value-history`, {
      params: HttpUtils.getHttpParams({type})
    });
  }

  valueHistoryPost(bridgeAdUnitId: string, data: AdUnitValueHistory[]): Observable<AdUnitValueHistory[]> {
    return this.httpClient.post<AdUnitValueHistory[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/value-history`, data);
  }

  valueHistoryDelete(bridgeAdUnitId: string, id: number) {
    return this.httpClient.delete(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/value-history/${id}`);
  }

  bidderRevshareGet(bridgeAdUnitId: string): Observable<AdUnitBidderRevshare[]> {
    return this.httpClient.get<AdUnitBidderRevshare[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/bidder-revshare`);
  }

  bidderRevsharePost(bridgeAdUnitId: string, data: AdUnitBidderRevshare[]): Observable<AdUnitBidderRevshare[]> {
    return this.httpClient.post<AdUnitBidderRevshare[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/bidder-revshare`, data);
  }

  bidderRevshareDelete(bridgeAdUnitId: string, id: number) {
    return this.httpClient.delete(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/bidder-revshare/${id}`);
  }

  bidderCostFactorGet(bridgeAdUnitId: string): Observable<AdUnitBidderCostFactor[]> {
    return this.httpClient.get<AdUnitBidderCostFactor[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/bidder-cost-factor`);
  }

  bidderCostFactorPost(bridgeAdUnitId: string, data: AdUnitBidderCostFactor[]): Observable<AdUnitBidderCostFactor[]> {
    return this.httpClient.post<AdUnitBidderCostFactor[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/bidder-cost-factor`, data);
  }

  bidderCostFactorDelete(bridgeAdUnitId: string, id: number) {
    return this.httpClient.delete(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/bidder-cost-factor/${id}`);
  }

  changeHealthyStatus(bridgeAdUnitId: string, healthyStatus: boolean): Observable<BridgeAdUnit> {
    return this.httpClient.put<BridgeAdUnit>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/unit-healthy?healthy=${healthyStatus}`, {});
  }

  changeHealthyStatusPost(bridgeAdUnitId: string, healthyStatus: boolean): Observable<BridgeAdUnit> {
    return this.httpClient.put<BridgeAdUnit>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/unit-post-health-check?healthy=${healthyStatus}`, {});
  }

  changeFlag(bridgeAdUnitId: string, flag: boolean): Observable<BridgeAdUnit> {
    return this.httpClient.put<BridgeAdUnit>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/flag?flag=${flag}`, {});
  }

  sendReadyEmails(IDs: string[]): Observable<BridgeAdUnit> {
    return this.httpClient.post<BridgeAdUnit>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/publisher-ready-email`, IDs);
  }



  bidIncrementGet(bridgeAdUnitId: string): Observable<AdUnitBidIncrement[]> {
    return this.httpClient.get<AdUnitBidIncrement[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/bid-increment`);
  }

  bidIncrementPost(bridgeAdUnitId: string, data: AdUnitBidIncrement[]): Observable<AdUnitBidIncrement[]> {
    return this.httpClient.post<AdUnitBidIncrement[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/bid-increment`, data);
  }

  bidIncrementDelete(bridgeAdUnitId: string, id: number) {
    return this.httpClient.delete(`${environment.serviceUrl}/ad-stats/bridge-adu/ad-units/${bridgeAdUnitId}/bid-increment/${id}`);
  }

  bidParamChangeRequest(params: BidParamChangeRequestFilter): Observable<BidParamChangeRequest[]> {
    return this.httpClient.get<BidParamChangeRequest[]>(`${environment.serviceUrl}/ad-stats/bridge-adu/bidparam-change-request`, {
      params: HttpUtils.getHttpParams(params)
    });
  }

  approveBidParamChangeRequest(id: string, status: BidParamChangeStatus) {
    return this.httpClient.post(`${environment.serviceUrl}/ad-stats/bridge-adu/bidparam-change-request/${id}/resolve`, `"${status}"`, {headers: new HttpHeaders({'Content-Type': 'application/json'})});
  }

  requestBidParamChange(id: string, params: BidParamChangeRequest) {
    return this.httpClient.post(`${environment.serviceUrl}/ad-stats/bridge-adu/${id}/bidparam-change-request`, params);
  }
}

